import React, { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar,
  Divider,
  Modal,
  Slider,
  IconButton
} from '@mui/material';
import { CloudUpload, GetApp, Close, RotateLeft, RotateRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Button from '@msi/cobalt-react/button';

const PanelFrameGenerator = () => {
  const theme = useTheme();
  const user = useSelector((state) => state.login);
  const users = useSelector((state) => state.users.users);
  const scoring = useSelector((state) => state.scoring.scoring) || [];
  const achievements = useSelector((state) => state.achievements.achievements) || [];
  const badges = useSelector((state) => state.scoring.badges) || [];
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [generatedFrameUrl, setGeneratedFrameUrl] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropSettings, setCropSettings] = useState({
    zoom: 1,
    x: 0,
    y: 0,
    rotation: 0
  });
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // Calculate user's AI level based on total score
  const getUserLevel = () => {
    const lastAchievement = achievements[achievements.length - 1];
    const badge = badges.find(b => b.id === lastAchievement?.badgeId);
    return badge || 'None';
  };

  const userLevel = getUserLevel();


  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
        setShowCropModal(true);
        setCropSettings({ zoom: 1, x: 0, y: 0, rotation: 0 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSelectFromComputer = () => {
    fileInputRef.current?.click();
  };

  const applyCrop = () => {
    if (!previewUrl) return;

    const canvas = cropCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    if (!img) return;

    const size = 300;
    canvas.width = size;
    canvas.height = size;

    ctx.clearRect(0, 0, size, size);
    
    // Create circular clipping path
    ctx.save();
    ctx.beginPath();
    ctx.arc(size/2, size/2, size/2, 0, 2 * Math.PI);
    ctx.clip();

    // Calculate image dimensions and position
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let drawWidth = size * cropSettings.zoom;
    let drawHeight = size * cropSettings.zoom;

    if (imgAspect > 1) {
      drawHeight = drawWidth / imgAspect;
    } else {
      drawWidth = drawHeight * imgAspect;
    }

    const drawX = (size / 2) - (drawWidth / 2) + cropSettings.x;
    const drawY = (size / 2) - (drawHeight / 2) + cropSettings.y;

    // Apply rotation if needed
    if (cropSettings.rotation !== 0) {
      ctx.translate(size/2, size/2);
      ctx.rotate((cropSettings.rotation * Math.PI) / 180);
      ctx.translate(-size/2, -size/2);
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();

    // Convert to blob and create URL
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setCroppedImageUrl(url);
      setShowCropModal(false);
      // Reset file input value so user can select the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }, 'image/png');
  };

  const handleCloseCropModal = () => {
    setShowCropModal(false);
    // Reset file input value so user can select the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageDrag = (e) => {
    if (e.buttons === 1) { // Left mouse button
      const rect = e.currentTarget.getBoundingClientRect();
      const deltaX = e.movementX;
      const deltaY = e.movementY;
      
      setCropSettings(prev => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
    }
  };

  const generateFrame = () => {
    if (!croppedImageUrl) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (standard profile picture size)
    const size = 400;
    canvas.width = size;
    canvas.height = size;

    const frameImg = new Image();
    frameImg.crossOrigin = 'anonymous';
    frameImg.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, size, size);
      
      if (croppedImageUrl) {
        const userImg = new Image();
        userImg.crossOrigin = 'anonymous';
        userImg.onload = () => {
          // Draw user image (already cropped to circle)
          ctx.drawImage(userImg, (size - 300) / 2, (size - 300) / 2, 300, 300);
          
          // Draw frame on top
          ctx.drawImage(frameImg, 0, 0, size, size);
          
          // Convert to blob and create download URL
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            setGeneratedFrameUrl(url);
          }, 'image/png');
        };
        
        userImg.src = croppedImageUrl;
      } else {
        // Just the frame
        ctx.drawImage(frameImg, 0, 0, size, size);
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setGeneratedFrameUrl(url);
        }, 'image/png');
      }
    };

    frameImg.src = `/${userLevel.id.toUpperCase()}-frame.png`;
  };

  const downloadFrame = () => {
    if (generatedFrameUrl) {
      const link = document.createElement('a');
      link.href = generatedFrameUrl;
      link.download = `ai-${userLevel.badge.toLowerCase()}-frame.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadBackground = () => {
    const backgroundUrl = `/${userLevel.id.toUpperCase()}-background.jpg`;
    const link = document.createElement('a');
    link.href = backgroundUrl;
    link.download = `ai-${userLevel.badge.toLowerCase()}-background.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.text.main, padding: 2, height: 'auto' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Digital Swag
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
            Create a profile picture that reflects your expertise with AI. 
            Please upload your photo, generate a custom avatar, and download it to showcase on platforms such as your Google account.
        </Typography>

        {userLevel === 'None' ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.secondary }}>
                    No AI Level Yet
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, maxWidth: 600, mx: 'auto' }}>
                    You need to reach an AI level before you can create frames. 
                    Complete some AI-related activities and earn achievements to unlock your first AI level, 
                    then come back here to create your personalized profile frame!
                </Typography>
            </Box>
        ) : (
            <>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Your AI Level is: <span style={{ color: '#2196f3', fontWeight: 600 }}>AI {userLevel.badge}</span>
                    </Typography>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Upload Your Profile Picture
                </Typography>

                <Paper
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragOver ? theme.palette.primary.main : '#ccc',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        mb: 3,
                        backgroundColor: isDragOver ? theme.palette.action.hover : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        maxWidth: 800,
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: theme.palette.action.hover,
                        }
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleSelectFromComputer}
                >
                    <CloudUpload sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.secondary }}>
                        Drag and drop image here
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                        <Button 
                            variant="contained" 
                        >
                            Select from Computer
                        </Button>
                    </Box>
                    
                    {croppedImageUrl && (
                        <Box sx={{ mt: 3 }}>
                            <Avatar 
                                src={croppedImageUrl} 
                                sx={{ 
                                    width: 100, 
                                    height: 100, 
                                    mx: 'auto',
                                    border: '3px solid',
                                    borderColor: theme.palette.primary.main
                                }} 
                            />
                        </Box>
                    )}
                </Paper>

                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                />

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={generateFrame}
                        disabled={!croppedImageUrl}
                        sx={{
                            backgroundColor: '#4dd0e1',
                            color: 'white',
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            '&:hover': {
                                backgroundColor: '#26c6da',
                            },
                            '&:disabled': {
                                backgroundColor: '#ccc',
                            }
                        }}
                    >
                        Generate Frame
                    </Button>
                </Box>

                {/* Google Meet Background Section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Divider sx={{ mb: 3 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Google Meet Background
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary, maxWidth: 600, mx: 'auto' }}>
                        Download a custom Google Meet background that showcases your AI level during video calls.
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <img 
                            src={`/${userLevel.id.toUpperCase()}-background.jpg`}
                            alt={`AI ${userLevel.badge} background preview`}
                            style={{ 
                                maxWidth: 400, 
                                maxHeight: 225, 
                                borderRadius: 8,
                                border: '2px solid #007ab2',
                                objectFit: 'cover'
                            }} 
                        />
                    </Box>
                    
                    <Button
                        variant="contained"
                        startIcon={<GetApp />}
                        onClick={downloadBackground}
                        sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            '&:hover': {
                                backgroundColor: '#388e3c',
                            }
                        }}
                    >
                        Download Background
                    </Button>
                </Box>

                {generatedFrameUrl && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="h6" gutterBottom>
                            Your Generated Frame
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                            <img 
                                src={generatedFrameUrl} 
                                alt="Generated frame" 
                                style={{ 
                                    maxWidth: 300, 
                                    maxHeight: 300, 
                                    borderRadius: '50%',
                                    border: '3px solid #007ab2'
                                }} 
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<GetApp />}
                            onClick={downloadFrame}
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                }
                            }}
                        >
                            Download Frame
                        </Button>
                    </Box>
                )}
            </>
        )}

        {/* Hidden canvas for image generation */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <canvas ref={cropCanvasRef} style={{ display: 'none' }} />

        {/* Crop Modal */}
        <Modal
            open={showCropModal}
            onClose={handleCloseCropModal}
            aria-labelledby="crop-modal-title"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 500,
                bgcolor: 'background.paper',
                border: '2px solid #000',
                boxShadow: 24,
                p: 4,
                borderRadius: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography id="crop-modal-title" variant="h6" component="h2">
                        Edit photo
                    </Typography>
                    <IconButton onClick={handleCloseCropModal}>
                        <Close />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Left side - Image crop area */}
                    <Box sx={{ flex: 1 }}>
                        <Box 
                            sx={{
                                width: 300,
                                height: 300,
                                position: 'relative',
                                backgroundColor: '#f0f0f0',
                                borderRadius: 2,
                                overflow: 'hidden',
                                cursor: 'move'
                            }}
                            onMouseMove={handleImageDrag}
                            onWheel={e => {
                                e.preventDefault();
                                setCropSettings(prev => {
                                    let newZoom = prev.zoom + (e.deltaY < 0 ? 0.1 : -0.1);
                                    newZoom = Math.max(0.5, Math.min(3, newZoom));
                                    return { ...prev, zoom: newZoom };
                                });
                            }}
                        >
                            {previewUrl && (
                                <>
                                    <img
                                        ref={imageRef}
                                        src={previewUrl}
                                        alt="Crop preview"
                                        style={{
                                            position: 'absolute',
                                            width: `${300 * cropSettings.zoom}px`,
                                            height: 'auto',
                                            left: `${150 - (150 * cropSettings.zoom) + cropSettings.x}px`,
                                            top: `${150 - (150 * cropSettings.zoom) + cropSettings.y}px`,
                                            transform: `rotate(${cropSettings.rotation}deg)`,
                                            transformOrigin: 'center',
                                            userSelect: 'none',
                                            pointerEvents: 'none'
                                        }}
                                        draggable={false}
                                    />
                                    {/* Circular overlay */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        border: '3px solid #fff',
                                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                                        pointerEvents: 'none'
                                    }} />
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Right side - Controls */}
                    <Box sx={{ width: 150 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" gutterBottom>
                                Zoom
                            </Typography>
                            <Slider
                                value={cropSettings.zoom}
                                onChange={(e, newValue) => setCropSettings(prev => ({ ...prev, zoom: newValue }))}
                                min={0.5}
                                max={3}
                                step={0.1}
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" gutterBottom>
                                Straighten
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton 
                                    size="small" 
                                    onClick={() => setCropSettings(prev => ({ ...prev, rotation: prev.rotation - 15 }))}
                                >
                                    <RotateLeft />
                                </IconButton>
                                <Slider
                                    value={cropSettings.rotation}
                                    onChange={(e, newValue) => setCropSettings(prev => ({ ...prev, rotation: newValue }))}
                                    min={-45}
                                    max={45}
                                    step={1}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                                <IconButton 
                                    size="small"
                                    onClick={() => setCropSettings(prev => ({ ...prev, rotation: prev.rotation + 15 }))}
                                >
                                    <RotateRight />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button variant="outlined" onClick={handleCloseCropModal}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={applyCrop}>
                        Save photo
                    </Button>
                </Box>
            </Box>
        </Modal>
    </Box>
);
};

export default PanelFrameGenerator;
